import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { Component, NgZone, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Validators, FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import { ApiConstants } from 'src/app/base/api-constants';
import { RegexPatternConstants } from 'src/app/base/regex-pattern.constants';
import { environment } from 'src/environments/environment';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { UploadAttachmentService } from 'src/app/services/upload-attachment.service';
import * as FileAttachmentsAction from '../../../STORE/actions/fileattachments.actions';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { DatePipe } from '@angular/common';
import { fileAttachmentsDataOrder } from 'src/app/STORE/selectors/fileattachments.selector';
import { userData } from 'src/app/STORE/selectors/auth.selector';
import { TranslateService } from '@ngx-translate/core';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';

import { UtilService } from 'src/app/services/util.service';
import { debounce } from 'lodash-es';

export enum faNodeType {
  UPLOAD = 'upload',
  LINK = 'link',
  GROUP = 'group'
}
@AutoUnsubscribe({ arrayName: 'subscriptions' })
@Component({
  selector: 'vb-file-attachments',
  templateUrl: './file-attachments.component.html',
  styleUrls: ['./file-attachments.component.scss'],
  standalone: false  //NOSONAR
})
export class FileAttachmentsComponent implements OnInit, OnDestroy {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private store = inject(Store);
  private appGlobalService = inject(AppGlobalService);
  private translate = inject(TranslateService);
  private zone = inject(NgZone);
  private uploadService = inject(UploadAttachmentService);
  private formBuilder = inject(FormBuilder);
  private datePipe = inject(DatePipe);
  private utilService = inject(UtilService);

  @ViewChild('futree')
  public futree;
  filesToUpload: File[] | null = null;
  showLink = false;
  appId = '';
  messages: any[] = [];
  errorMessage: any = '';
  sidToBeSent: any;
  errorFlag = false;
  orgId = '';
  doc: any = null;
  hasDoc = false;
  attachmentsOrder: any[] = [];
  isLoading = false;
  initialSave = false;
  attachmentsOrderRes: any;
  subscriptions: Subscription[] = [];
  linkDetails = this.formBuilder.group({
    linkName: ['', [Validators.required, Validators.pattern(RegexPatternConstants.linkName)]],
    linkUrl: ['', [Validators.required, Validators.pattern(RegexPatternConstants.generalUrl)]]
  });
  user = '';
  destroy$: Subject<boolean> = new Subject<boolean>();
  submitted = false;
  action = 'save';
  linkHeader = 'Add Link';
  direction: any = 'ascending';
  updateSidLink = '';
  orderSid = '';
  searchText = '';
  sortDirection = 'asc';
  disallowEdit = false;
  fileExtensionBlacklist: string[] = [
    'aspx',
    'asp',
    'css',
    'swf',
    'xhtml',
    'rhtml',
    'shtml',
    'jsp',
    'js',
    'pl',
    'php',
    'cgi',
    'exe',
    'sh',
    'bash',
    'zsh',
    'py'
  ];
  dropDownOpen = false;
  itemType: string;

  initFileUploadTree(files) {
    const $$: any = $;
    const _self = this;
    if (this.futree.nativeElement) {
      $(':ui-fancytree').fancytree('destroy');
    }
    $('#futree').fancytree({
      treeId: '1',
      source: files,
      autoScroll: true,
      extensions: ['edit', 'dnd5', 'table', 'gridnav', 'contextMenu', 'filter'],
      tooltip: true,
      activate: function (e, data) {
        /* empty */
      },
      filter: {
        autoApply: true, // Re-apply last filter if lazy data is loaded
        autoExpand: false, // Expand all branches that contain matches while filtered
        counter: true, // Show a badge with number of matching child nodes near parent icons
        fuzzy: false, // Match single characters in order, e.g. 'fb' will match 'FooBar'
        hideExpandedCounter: true, // Hide counter badge if parent is expanded
        hideExpanders: false, // Hide expanders if all child nodes are hidden by filter
        highlight: true, // Highlight matches by wrapping inside <mark> tags
        leavesOnly: false, // Match end nodes only
        nodata: true, // Display a 'no data' status node if result is empty
        mode: 'hide' // Grayout unmatched nodes (pass "hide" to remove unmatched node instead. pass "dimm" to dim unmatched nodes)
      },
      edit: {
        adjustWidthOfs: null,
        triggerStart: ['clickActive', 'dblclick', 'f2', 'mac+enter', 'shift+click'],
        beforeEdit: function (event: any, data: any) {
          // Return false to prevent edit mode
          if (data.node.type !== faNodeType.GROUP || _self.disallowEdit) {
            return false;
          }
          return true;
        },
        edit: function (event: any, data: any) {
          // Editor was opened (available as data.input)
        },
        beforeClose: function (event: any, data: any) {
          // Return false to prevent cancel/save (data.input is available)
        },
        save: function (event: any, data: any) {
          // Save data.input.val() or return false to keep editor open
          // console.log('save...', this, data);
          // Simulate to start a slow ajax request...
          setTimeout(function () {
            $(data.node.span).removeClass('pending');
            data.node.setTitle(data.node.title);
          }, 2000);
          return true;
        },
        close: function (event: any, data: any) {
          // Editor was removed
          if (data.save) {
            // Since we started an async request, mark the node as preliminary
            $(data.node.span).addClass('pending');
          }
        }
      },
      contextMenu: {
        menu: function (node) {
          const menu: any = {};
          if (!_self.disallowEdit) {
            menu.addGroup = { name: _self.translate.instant('ADD_GROUP'), icon: 'add' };
            menu.delete = { name: _self.translate.instant('DELETE'), icon: 'delete', disabled: false };
          }
          return menu;
        },
        actions: function (node, action, options) {
          const id = uuidv4();
          switch (action) {
            case 'addGroup':
              node.editCreateNode('after', {
                type: faNodeType.GROUP,
                sid: id,
                key: id,
                folder: true,
                title: ''
              });
              break;
            case 'delete':
              _self.zone.run(() => {
                this.confirmDialog(node, 'treeNode');
              });
              break;
          }
        }
      },
      dnd5: {
        preventRecursion: true, // Prevent dropping nodes on own descendants
        // preventSameParent: true,
        preventVoidMoves: true,

        dragStart: function (node, data: any) {
          // Set the allowed effects (i.e. override the 'effectAllowed' option)
          data.effectAllowed = 'all';
          data.dropEffect = 'move';
          return true;
        },

        dragEnter: function (node, data) {
          return true;
        },
        dragOver: function (node, data) {
          // console.log('dragOver--data', data);
          // console.log('dragOver--node', node);
          // Assume typical mapping for modifier keys
          data.dropEffect = data.dropEffectSuggested;
          // data.dropEffect = "move";
        },
        dragDrop: function (node, data) {
          /* This function MUST be defined to enable dropping of items on
           * the tree.
           */
          let newNode,
            transfer = data.dataTransfer,
            sourceNodes = data.otherNodeList,
            mode = data.dropEffect;

          node.debug(
            `T1: dragDrop: effect=` +
            `data: ${data.dropEffect}/${data.effectAllowed}, dataTransfer: ${transfer.dropEffect}/${transfer.effectAllowed
            }`
          );

          if (data.hitMode === 'after') {
            // If node are inserted directly after tagrget node one-by-one,
            // this would reverse them. So we compensate:
            sourceNodes.reverse();
          }
          if (data.otherNode) {
            // console.log('data', data);
            // Drop another Fancytree node from same frame (maybe a different tree however)
            const sameTree = data.otherNode.tree === data.tree;

            if (mode === 'move') {
              data.otherNode.moveTo(node, data.hitMode);
            } else {
              newNode = data.otherNode.copyTo(node, data.hitMode);
              if (mode === 'link') {
                newNode.setTitle(`Link to ${newNode.title}`);
              } else {
                newNode.setTitle(`Copy of ${newNode.title}`);
              }
            }
          } else {
            // Drop a non-node
            node.addNode({ title: transfer.getData('text') }, data.hitMode);
          }
          node.setExpanded();
          const tree = $.ui.fancytree.getTree('#futree');
          const d = tree.toDict(true);
          let treeData: any = [];
          if (d.title === 'root') {
            treeData = [...d.children];
          }
          _self.saveFancyTreeOrder(treeData);
        }
      },
      table: {
        indentation: 40,
        nodeColumnIdx: 1,
        checkboxColumnIdx: 0
      },
      gridnav: {
        autofocusInput: false,
        handleCursorKeys: true
      },
      createNode: function (event, data) {
        const node = data.node,
          $tdList = $(node.tr).find('>td');
        if (node.statusNodeType === 'nodata') {
          $tdList.html('No Records Found');
        }

        // Span the remaining columns if it's a folder.
        // We can do this in createNode instead of renderColumns, because
        // the `isFolder` status is unlikely to change later
        if (node.isFolder()) {
          $tdList.eq(1).prop('colspan', 6).nextAll().remove();
        }
      },
      renderColumns: function (event, data) {
        const node: any = data.node;
        const id = node.data.sid;
        $(node.tr).attr('id', id);
        if (node.type !== faNodeType.GROUP) {
          const $tdList = $(node.tr).find('>td');
          if ($tdList.eq(0).find('.image-ext').length === 0) {
            const title = node.title.split('.');
            const ext = title[title.length - 1];
            if (node.type === faNodeType.UPLOAD) {
              // if(["jpeg","png","jpg","gif", "webp"].indexOf(ext.toLowerCase())>-1){
              //   $tdList.eq(0).append(`<span class="image-ext ${ext}"><img src="${node.data.url}"/></span>`);
              // }else{
              $tdList.eq(0).append(`<span class="image-ext ${ext}"></span>`);
              // }
            } else if ($tdList.eq(0).find('.pi-external-link').length === 0) {
              $tdList.eq(0).append(`<span class="link"><i class="pi pi-external-link"></i></span>`);
            }
          }
          if ($tdList.eq(2)[0].innerHTML === '') {
            $tdList.eq(2).html(node.data.owner);
          }
          if ($tdList.eq(3)[0].innerHTML === '') {
            const nodeDate: any = _self.datePipe.transform(node.data.date, 'dd-MMM-yyyy');
            $tdList.eq(3).html(nodeDate);
          }
          if ($tdList.eq(4).find('.status').length === 0) {
            if (!_self.disallowEdit) {
              $tdList.eq(4).append(`
              <div class="image-text-container d-flex align-item-center ${node.data.status}">
                <span></span>
                <p class="text">${node.data.status}</p>
              </div>
              <div class="dropdown status d-none">
              <button class="btn dropdown-toggle" type="button" id="status-${id}" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
              ${node.data.status}</button>
                <div class="dropdown-menu" aria-labelledby="status-${id}">
                  <span class="dropdown-item">Draft</span>
                  <span class="dropdown-item">Under Review</span>
                  <span class="dropdown-item">Finalized</span>
                </div>
              </div>`);
              $tdList
                .eq(4)
                .find('.image-text-container')
                .on('click', function () {
                  const prevStatus = this.innerText;
                  $tdList.eq(4).find('.image-text-container').removeClass('d-flex');
                  $tdList.eq(4).find('.image-text-container').addClass('d-none');
                  $tdList.eq(4).find('.status').removeClass('d-none');
                  $tdList
                    .eq(4)
                    .find('.status .dropdown-menu .dropdown-item')
                    .off('click')
                    .on('click', function () {
                      const parentEle = $(this).parent().prev('button');
                      $(parentEle).removeClass('Under Review');
                      $(parentEle).removeClass('Draft');
                      $(parentEle).removeClass('Finalized');
                      const currowid = this.closest('tr')?.id;
                      $(`#status-${currowid}`).html(this.innerText);
                      _self.onchangeStatus(node, this.innerText);
                      $tdList.eq(4).find('.status').addClass('d-none');
                      $tdList.eq(4).find('.image-text-container').removeClass('d-none');
                      $tdList.eq(4).find('.image-text-container').addClass('d-flex');
                      $tdList.eq(4).find('.image-text-container').removeClass('d-none');
                      $tdList.eq(4).find('.image-text-container').removeClass(prevStatus);
                      $tdList.eq(4).find('.image-text-container').addClass(this.innerText);
                      $tdList.eq(4).find('.image-text-container').find('.text').text(this.innerText);
                    });
                });
            } else {
              $tdList.eq(4).append(`<div class="dropdown status">
              <button class="btn dropdown-toggle ${node.data.status}" type="button" id="status-${id}" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" disabled>
              ${node.data.status}</button>
              </div>`);
            }
          }
          if ($tdList.eq(5)) {
            $tdList.eq(5).addClass('text-center actions-icons');
            if (!_self.disallowEdit) {
              if (node.type === faNodeType.LINK && $tdList.eq(5).find('.edit').length === 0) {
                $tdList.eq(5).append(`<span class="edit custom-tooltip cursor-pointer">
                <i class="tooltip-arrow"></i><i class="tooltiptext"></i>
                </span>`);
                $tdList
                  .eq(5)
                  .find('.edit')
                  .off('click')
                  .on('click', function (event) {
                    _self.showLinkModal('edit', node);
                  });
              }
              if ($tdList.eq(5).find('.delete').length === 0) {
                $tdList.eq(5).append(`
               <span class="delete custom-tooltip cursor-pointer" >
               <i class="tooltip-arrow"></i><i class="tooltiptext"></i>
               </span>
               `);
                $tdList
                  .eq(5)
                  .find('.delete')
                  .off('click')
                  .on('click', function (event) {
                    _self.confirmDialog(node, 'treeNode');
                  });
              }
            }
          }
        }
      },
      modifyChild: function (event: any, data: any) {
        data.tree.info(event.type, data);
      },
      click: (event: any, data: any) => {
        // console.log('node clicked: ', event, data.node.data);
        const originalTarget = event.originalEvent?.target;
        if (` ${originalTarget.className} `.replace(/[\n\t]/g, ' ').indexOf(' fancytree-title ') > -1) {
          this.downloadDoc(data.node);
        }
        return true;
      }
    });
    $('input[name=search]').on('keyup', function (e) {
      const fancyTree: any = $.ui.fancytree;
      let n,
        tree = fancyTree.getTree('#futree'),
        args = 'autoApply autoExpand fuzzy hideExpanders highlight leavesOnly nodata'.split(' '),
        opts: any = {},
        filterFunc = tree.filterNodes,
        match: any = $(this).val();

      $.each(args, function (i, o) {
        opts[o] = $(`#${o}`).is(':checked');
      });
      opts.mode = 'hide';
      if ((e && e.which === $.ui.keyCode.ESCAPE) || $.trim(match) === '') {
        $('#btnResetSearch').trigger('click');
        $('#btnResetSearch').addClass('d-none');
        return;
      }
      $('#btnResetSearch').removeClass('d-none');

      n = filterFunc.call(
        tree,
        function (node) {
          const id = node.data.sid;
          const titleMat = new RegExp(match, 'i').test(node.title);
          console.log(node.title, titleMat);
          const statusMat = new RegExp(match, 'i').test(node.data.status);
          const ownerMat = new RegExp(match, 'i').test(node?.data?.owner);
          return titleMat || statusMat || ownerMat;
        },
        opts
      );
      // $("span#matches").text("(" + n + " matches)");
    });
    $('#btnResetSearch').click(function (e) {
      $('input[name=search]').val('');
      $('span#matches').text('');
      $('#btnResetSearch').addClass('d-none');
      $.ui.fancytree.getTree('#futree').clearFilter();
    });
  }

  toggleDropdown() {
    this.dropDownOpen = !this.dropDownOpen;
  }
  confirmDialog(data, option) {
    this.confirmationDialogService.confirm({
      message: this.translate.instant('DELETE_ATTATCHMENT_CONFIRMATION', { name: data.title }),
      header: 'Confirmation',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        switch (option) {
          case 'treeNode':
            this.deleteNode(data);
            break;
        }
      },
      reject: () => {
        /* empty */
      }
    });
  }

  viewDoc(node) {
    if (node.type === faNodeType.UPLOAD) {
      this.doc = node.data;
      // this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(node.data.url);
      this.hasDoc = true;
    } else if (node.type === faNodeType.LINK) {
      window.open(node.data.url, '_blank');
    }
  }

  saveFancyTreeOrder(order) {
    let fileAttachmentsData;
    let saveapiConstants;
    let sidToBeSent;
    let itemType;
    if (this.action === 'Delete' && this.itemType === 'attachment') {
      saveapiConstants = ApiConstants.deleteFileAttachmentsDataOrder;
      sidToBeSent = this.sidToBeSent;
      itemType = this.itemType;
    } else {
      saveapiConstants = this.initialSave
        ? ApiConstants.saveFileAttachmentsDataOrder
        : ApiConstants.updateFileAttachmentsDataOrder;
    }
    if (this.initialSave) {
      this.orderSid = uuidv4();
      this.attachmentsOrderRes = {
        appId: this.appId,
        orgId: this.orgId,
        sid: this.orderSid
      };
      fileAttachmentsData = {
        ...this.attachmentsOrderRes,
        ...{ orders: JSON.stringify(order) }
      };
    } else {
      fileAttachmentsData = {
        ...this.attachmentsOrderRes,
        ...{ orders: JSON.stringify(order) }
      };
    }
    console.log(this.action);
    let actionType;
    if (this.action === 'Delete') {
      actionType = 'Delete';
    }

    this.store.dispatch(
      FileAttachmentsAction.saveFileAttachmentsForOrder({
        saveapiConstants,
        fileAttachmentsData,
        itemType,
        sidToBeSent,
        actionType
      })
    );
    this.initialSave = false;
  }
  formNode(type, id, data) {
    if (data.fileName && data.fileName.length > 255) {
      this.utilService.showMessage('error', 'File Name should be lesser than 255 characters');
      return null;
    }
    const node = {
      title: type === faNodeType.UPLOAD ? data.fileName : this.linkDetails.controls.linkName.value,
      key: id,
      sid: id,
      folder: false,
      type: type === faNodeType.UPLOAD ? faNodeType.UPLOAD : faNodeType.LINK,
      url:
        type === faNodeType.UPLOAD
          ? `${environment.apihost}/attachments/download/attachment/${data.sid}/${this.appId}`
          : this.linkDetails.controls.linkUrl.value,
      data: {
        owner: this.user,
        date: new Date(),
        status: 'Draft'
      }
    };
    return node;
  }

  uploadSelectedFiles(evt) {
    this.filesToUpload = [...evt.files];
    this.isLoading = true;
    this.action = 'upload';

    for (let i = 0; i < this.filesToUpload.length; i++) {
      let curFile = this.filesToUpload[i];
      if (curFile.size > 33554432) {
        this.utilService.showMessage('error', 'File size should not exceed 32MB');
        this.isLoading = false;
        return;
      }
      let fileNameSplit = curFile.name.split(".");
      curFile['modelName'] = 'AppFileAttachment';
      curFile['fieldName'] = 'contentAttachments';

      let blackListedExtensionInCurrentFile = fileNameSplit.filter(value => this.fileExtensionBlacklist.includes(value));
      if (blackListedExtensionInCurrentFile.length != 0) {
        this.utilService.showMessage('error', 'Malicious file detected. File with problem : ' + curFile.name);
        this.isLoading = false;
        return;
      }
    }

    let fileBasicData = {
      type: faNodeType.UPLOAD,
      folder: false,
      data: {
        owner: this.user,
        date: new Date(),
        status: 'Draft'
      }
    }

    let curFile = this.filesToUpload;
    let id = this.attachmentsOrderRes?.sid;

    const url = `${environment.apihost}/appfileattachments/attachment/upload`
    let uploadsub = this.uploadService
      .uploadFile(curFile, url, '', id, '', fileBasicData)
      .subscribe((data: any) => {
        this.isLoading = false;
        this.fetchFileAttachments();
      }, (err: any) => {
        this.isLoading = false;
      });
    this.subscriptions.push(uploadsub);
    this.filesToUpload = [];
  }

  showLinkModal(action, node?: any) {
    this.errorMessage = '';
    this.errorFlag = false;
    console.log('action');
    console.log(action);
    this.showLink = true;
    this.action = action;
    if (action === 'edit') {
      this.linkDetails.setValue({
        linkName: node.title,
        linkUrl: node.data.url
      });
      this.linkHeader = 'Edit Link';
      this.updateSidLink = node.data.sid;
    } else {
      this.linkHeader = 'Add Link';
    }
  }

  downloadDoc(node) {
    if (node.type === faNodeType.LINK) {
      window.open(node.data.url, '_blank');
    } else if (node.type === faNodeType.UPLOAD) {
      const match = node.data.url?.match(/attachments\/download\/attachment\/([^\/]+)\//);
      const attachmentId = match && match[1] !== 'undefined' ? match[1] : node.data.sid;
      const downloadUrl = `${ApiConstants.getFileContents.url}${attachmentId}/${this.appId}`;
      this.subscriptions.push(
        this.uploadService.downloadFile(downloadUrl).subscribe(
          (blob: Blob) => {
            const url = globalThis.URL.createObjectURL(blob);
            const a = Object.assign(document.createElement('a'), { href: url, download: node.title });
            a.click();
            globalThis.URL.revokeObjectURL(url);
          },
          () => this.utilService.showMessage('error', this.translate.instant('DOWNLOAD_FILE_FAILED'))
        )
      );
    }
  }

  checkDupName(nodes, val, id) {
    let isduplicate = false;
    nodes.forEach((node) => {
      if (node.type === faNodeType.LINK) {
        if (node.title.toLowerCase() === val.toLowerCase() && node.data.sid !== id) {
          isduplicate = true;
          return;
        }
      } else if (node.type === faNodeType.GROUP) {
        if (node.children) {
          isduplicate = this.checkDupName(node.children, val, id);
          if (isduplicate) {
            return;
          }
        }
      }
    });
    return isduplicate;
  }

  sortFunction(field) {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.direction = this.direction === 'ascending' ? 'descending' : 'ascending';
    const params = {
      appid: this.appGlobalService.get('appId'),
      field: field,
      direction: this.direction
    };
    this.store.dispatch(
      FileAttachmentsAction.getFileAttachmentsWithSorting({
        apiConstants: ApiConstants.getFileAttachmentsDataOrderWithSorting,
        params: params
      })
    );
    this.isLoading = true;
  }

  onUpdateLink(id) {
    this.messages = [];
    this.submitted = true;
    if (this.linkDetails.get('linkName')?.hasError('required')) {
      this.messages.push('Link Name is required');
    }
    if (this.linkDetails.get('linkName')?.hasError('pattern')) {
      this.messages.push('Link Name is invalid');
    }
    if (this.linkDetails.get('linkUrl')?.hasError('required')) {
      this.messages.push('Link Url is required');
    }
    if (this.linkDetails.get('linkUrl')?.hasError('pattern')) {
      this.messages.push('Link Url is invalid');
    }
    if (this.messages.length > 0) {
      this.errorMessage = this.messages?.join('<br/>');
      this.errorFlag = true;
      return;
    }
    const tree = $.ui.fancytree.getTree('#futree');
    if (tree) {
      const rootNode = tree.rootNode?.children;
      if (this.checkDupName(rootNode, this.linkDetails.get('linkName')?.value, id)) {
        this.messages.push('Link Name Already Exists');
      }
      if (this.messages.length > 0) {
        this.errorMessage = this.messages?.join('<br/>');
        this.errorFlag = true;
        return;
      }
    }
    if (this.linkDetails.valid) {
      if (this.action === 'edit') {
        const linkNode = this.formNode(faNodeType.LINK, id, '');
        if (linkNode === null) {
          return;
        }
        console.log(linkNode);
        const updatedOrder = this.attachmentsOrder.map((obj) => {
          return obj.sid === id ? { ...obj, ...linkNode } : obj;
        });
        console.log(updatedOrder);
        this.attachmentsOrder = [...updatedOrder];
        const tree = $.ui.fancytree.getTree('#futree');
        const node = tree.getNodeByKey(id);
        node.setTitle(this.linkDetails.controls.linkName.value ?? '');
        node.data.url = this.linkDetails.controls.linkUrl.value;
        this.updateSidLink = '';
      } else {
        const id = uuidv4();
        const linkNode = this.formNode(faNodeType.LINK, id, '');
        if (linkNode === null) {
          return;
        }
        this.attachmentsOrder = [...this.attachmentsOrder, linkNode];
        if (this.attachmentsOrder.length === 1) {
          this.initFileUploadTree(this.attachmentsOrder);
        } else {
          $.ui.fancytree.getTree('#futree').getRootNode().addChildren(linkNode);
        }
      }
      this.saveFancyTreeOrder(this.attachmentsOrder);
      this.linkDetails.reset();
      this.showLink = false;
      this.submitted = false;
    }
  }
  removeChildNodes(node) {
    while (node.hasChildren()) {
      node.children.forEach((child) => {
        child.remove();
      });
    }
  }
  deleteNode(node) {
    this.action = 'Delete';
    if (node.type === faNodeType.GROUP) {
      if (node.children && node.children.length > 0) {
        node.children.map((e) => {
          const sid = e?.data?.sid;
          const ind = this.attachmentsOrder.findIndex((obj) => {
            return obj.sid === sid;
          });
          this.attachmentsOrder.splice(ind, 1);
        });
        this.removeChildNodes(node);
      }
    } else {
      const sid = node?.data?.sid;
      const ind = this.attachmentsOrder.findIndex((obj) => {
        return obj.sid === sid;
      });
      this.sidToBeSent = sid;
      this.itemType = node.type == 'link' ? 'link' : 'attachment';
      if (this.itemType === 'link') {
        this.attachmentsOrder.splice(ind, 1);
      }
    }
    if (this.itemType != 'attachment') {
      const referenceNode = node.getParent();
      node.remove();
      referenceNode.setActive();
      this.saveFancyTreeOrder(this.attachmentsOrder);
    } else {
      this.store.dispatch(
        FileAttachmentsAction.deleteFileAttachments({
          apiConstants: ApiConstants.deleteFileAttachmentsDataOrder,
          fileAttachmentsId: this.sidToBeSent
        })
      );
    }
  }
  onchangeStatus(node, value) {
    const id = node.data.sid;
    const updatedOrder = this.attachmentsOrder.map((obj) => {
      return obj.sid === id ? { ...obj, data: { ...obj.data, status: value } } : obj;
    });
    this.attachmentsOrder = [...updatedOrder];
    this.saveFancyTreeOrder(this.attachmentsOrder);
  }
  ngOnInit(): void {
    this.appId = this.appGlobalService.get('appId');
    this.orgId = this.appGlobalService.get('orgId');
    if (!this.utilService.checkEditPermission('attachments')) {
      this.disallowEdit = true;
    }
    this.isLoading = true;
    this.store
      .select(fileAttachmentsDataOrder)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.isLoading = false;
        this.initialSave = !res?.sid; // to check whether sid is saved already
        if (res) {
          this.attachmentsOrderRes = res;
          let localAttachmentsOrder;
          if (res.orders) {
            localAttachmentsOrder = JSON.parse(res.orders);
            this.attachmentsOrder = localAttachmentsOrder;
          } else {
            localAttachmentsOrder = [];
            this.attachmentsOrder = [];
          }
          this.initFileUploadTree(localAttachmentsOrder);
          if (this.searchText) {
            setTimeout(() => {
              $('#searchTerm').keyup();
            }, 500);
          }
        }
      }, (err: any) => {
        this.isLoading = false;
      });
    this.store
      .select(userData)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: any) => {
        if (user) {
          this.user = `${user?.firstName} ${user?.lastName}`;
        }
      });
    this.fetchFileAttachments();
  }

  fetchFileAttachments() {
    this.store.dispatch(
      FileAttachmentsAction.getFileAttachments({
        apiConstants: ApiConstants.getFileAttachmentsDataOrder
      })
    );
    this.isLoading = true;
  }

  emailToName(email: string) {
    return this.utilService.getNameFromAppMetaInfo(email);
  }

  refresh(searchText) {
    this.store.dispatch(
      FileAttachmentsAction.getFileAttachments({
        apiConstants: ApiConstants.getFileAttachmentsDataOrder
      })
    );
    this.isLoading = true;
  }

  closeDialog() {
    this.showLink = false;
    this.submitted = false;
    this.linkDetails.reset();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
    $(':ui-fancytree').fancytree('destroy');
    $(this.futree.nativeElement).remove();
    $('tr').off('click');
    $('.status .dropdown-item').off('click');
    $('.actions-icons .edit').off('click');
    $('.actions-icons .delete').off('click');
  }

  addToSubscriptionsList(subName) {
    this.subscriptions.push(subName);
  }
}



